import type { TrackingRunStatus } from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import {
  CircleCheck,
  CircleX,
  Clock,
  Minus,
  TriangleAlert,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed, NothingYet } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { formatDate } from "@/components/table/cells";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  RUN_STATUS_LABEL,
  RUN_TRIGGER_LABEL,
  runNote,
} from "@/modules/orders/tracking";
import { useSourceNames } from "@/modules/settings/logistics/useSourceNames";

const STATUS_ICON: Record<TrackingRunStatus, ReactNode> = {
  ok: <CircleCheck aria-hidden />,
  partial: <TriangleAlert aria-hidden />,
  failed: <CircleX aria-hidden />,
  skipped: <Minus aria-hidden />,
  running: <Clock aria-hidden />,
};

const STATUS_CLASS: Record<TrackingRunStatus, string> = {
  ok: "border-transparent bg-tint-green text-tint-green-foreground",
  partial: "border-transparent bg-tint-yellow text-tint-yellow-foreground",
  failed: "border-destructive/40 bg-destructive/10 text-destructive",
  skipped: "border-border bg-muted text-muted-foreground",
  running: "border-transparent bg-tint-blue text-tint-blue-foreground",
};

/** The result of one run, in words and with an icon: colour is never the only signal. */
export function RunStatusBadge({ status }: { status: TrackingRunStatus }) {
  return (
    <Badge
      variant="outline"
      className={cn("gap-1.5 font-medium", STATUS_CLASS[status])}
    >
      {STATUS_ICON[status]}
      {RUN_STATUS_LABEL[status]}
    </Badge>
  );
}

const when = (value: string) => formatDate(value, true);

/**
 * Settings → Logistics partners, recent checks (#527, Part B): every run of
 * the poller, newest first, paged — when, which source, whether it was the timer
 * or a person, how many shipments it looked at, how many it changed, how many it
 * could not check, and in words what went wrong or why it checked nothing.
 *
 * This is where a failure is read: "expected failures are rows, not logs".
 * Nothing here is a payload or a key; a run's words are written to carry
 * neither.
 */
export function RecentChecks() {
  const data = useDataSource();
  const nameOf = useSourceNames();

  const [limit, setLimit] = useState(10);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const query = useQuery({
    queryKey: ["tracking", "runs", limit, cursor],
    queryFn: () => data.tracking.listRuns({ limit, cursor }),
  });

  return (
    <section aria-labelledby="runs-heading" className="space-y-3">
      <div className="max-w-prose">
        <h2 id="runs-heading" className="text-base font-semibold">
          Recent checks
        </h2>
        <p className="text-muted-foreground text-sm">
          Each time shipments were checked, newest first. A check that failed or
          found nothing to do says why.
        </p>
      </div>

      {query.isPending ? (
        <TableSkeleton rows={4} />
      ) : query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.data.items.length === 0 ? (
        <NothingYet
          title="No checks yet"
          description="The first check runs a minute after the server starts, or use Check now above."
        />
      ) : (
        <div className="space-y-2">
          {/* A table from `md` up and a card per run below it: eight columns
              on a phone is a table read by dragging it sideways. */}
          <div className="hidden overflow-x-auto rounded-md border md:block">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-muted/40">
                <tr className="border-b text-left">
                  <th className="px-3 py-2 font-medium whitespace-nowrap">
                    When
                  </th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Started by</th>
                  <th className="px-3 py-2 font-medium">Result</th>
                  <th className="px-3 py-2 text-right font-medium">Checked</th>
                  <th className="px-3 py-2 text-right font-medium">Changed</th>
                  <th className="px-3 py-2 text-right font-medium">Failed</th>
                  <th className="px-3 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {query.data.items.map((run) => (
                  <tr
                    key={run.id}
                    className="border-b align-top last:border-b-0"
                  >
                    <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                      {when(run.startedAt)}
                    </td>
                    <td className="px-3 py-2">{nameOf(run.source)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {RUN_TRIGGER_LABEL[run.trigger]}
                    </td>
                    <td className="px-3 py-2">
                      <RunStatusBadge status={run.status} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {run.checked}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {run.updated}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {run.failures}
                    </td>
                    <td className="max-w-xs px-3 py-2 wrap-anywhere">
                      {runNote(run) ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="divide-y rounded-md border md:hidden">
            {query.data.items.map((run) => (
              <li key={run.id} className="space-y-1.5 px-3 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium tabular-nums">
                    {when(run.startedAt)}
                  </span>
                  <RunStatusBadge status={run.status} />
                </div>
                <p className="text-muted-foreground text-sm">
                  {nameOf(run.source)} · {RUN_TRIGGER_LABEL[run.trigger]}
                </p>
                <p className="text-sm tabular-nums">
                  Checked {run.checked} · Changed {run.updated} · Failed{" "}
                  {run.failures}
                </p>
                {runNote(run) ? (
                  <p className="text-sm wrap-anywhere">{runNote(run)}</p>
                ) : null}
              </li>
            ))}
          </ul>

          <div className="rounded-md border px-3 py-1">
            <Pagination
              page={query.data}
              limit={limit}
              limitOptions={[10, 25, 50]}
              onLimitChange={(next) => {
                setLimit(next);
                setHistory([undefined]);
              }}
              history={history}
              onNavigate={setHistory}
            />
          </div>
        </div>
      )}
    </section>
  );
}
